import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertOctagon, Power, PowerOff, Pause, Play, Shield,
  AlertTriangle, Clock, History, X, Check, Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function KillSwitch({ onStatusChange }) {
  const [status, setStatus] = useState("active");
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [statusDetails, setStatusDetails] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/safety/kill-switch/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data.status);
      setStatusDetails(response.data);
      onStatusChange && onStatusChange(response.data.status);
    } catch (error) {
      console.error("Error fetching kill switch status:", error);
    }
  };

  const activateKillSwitch = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/safety/kill-switch/activate`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            reason: "user_requested",
            description: "Emergency stop activated by user"
          }
        }
      );

      setStatus("killed");
      setShowConfirm(false);
      onStatusChange && onStatusChange("killed");

      toast.error(
        <div>
          <p className="font-bold">🛑 EMERGENCY STOP ACTIVATED</p>
          <p className="text-sm mt-1">
            {response.data.affected.campaigns_paused} campaigns paused, {response.data.affected.posts_cancelled} posts cancelled
          </p>
        </div>,
        { duration: 10000 }
      );

      fetchStatus();
    } catch (error) {
      toast.error("Failed to activate kill switch");
    } finally {
      setIsLoading(false);
    }
  };

  const deactivateKillSwitch = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/safety/kill-switch/deactivate`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStatus("active");
      onStatusChange && onStatusChange("active");

      toast.success("✅ AI activities can now resume");
      fetchStatus();
    } catch (error) {
      toast.error("Failed to deactivate kill switch");
    } finally {
      setIsLoading(false);
    }
  };

  const pauseActivities = async (hours = 24) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/safety/kill-switch/pause`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { duration_hours: hours }
        }
      );

      setStatus("paused");
      onStatusChange && onStatusChange("paused");

      toast.info(`⏸️ AI activities paused for ${hours} hours`);
      fetchStatus();
    } catch (error) {
      toast.error("Failed to pause activities");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/safety/kill-switch/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.events);
      setShowHistory(true);
    } catch (error) {
      toast.error("Failed to fetch history");
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "active": return "bg-green-500";
      case "paused": return "bg-yellow-500";
      case "killed": return "bg-red-500";
      case "terminated": return "bg-gray-500";
      default: return "bg-gray-500";
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case "active": return <Power className="w-4 h-4" />;
      case "paused": return <Pause className="w-4 h-4" />;
      case "killed": return <PowerOff className="w-4 h-4" />;
      case "terminated": return <AlertOctagon className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  return (
    <>
      {/* Kill Switch Button */}
      <div className="flex items-center gap-3">
        {/* Status Indicator */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
          <span className={`w-2 h-2 rounded-full ${getStatusColor()} ${status === "active" ? "animate-pulse" : ""}`}></span>
          <span className="text-xs font-medium capitalize">{status}</span>
          {getStatusIcon()}
        </div>

        {/* Main Kill Switch Button */}
        {status === "active" ? (
          <Button
            variant="destructive"
            size="sm"
            className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            onClick={() => setShowConfirm(true)}
          >
            <AlertOctagon className="w-4 h-4 mr-2" />
            Kill Switch
          </Button>
        ) : status === "killed" ? (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full border-green-500 text-green-500 hover:bg-green-500/10"
            onClick={deactivateKillSwitch}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Resume
          </Button>
        ) : status === "paused" ? (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
            onClick={deactivateKillSwitch}
            disabled={isLoading}
          >
            <Play className="w-4 h-4 mr-2" />
            Resume Now
          </Button>
        ) : null}

        {/* Pause Button (only when active) */}
        {status === "active" && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => pauseActivities(24)}
          >
            <Pause className="w-4 h-4 mr-2" />
            Pause 24h
          </Button>
        )}

        {/* History Button */}
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full"
          onClick={fetchHistory}
        >
          <History className="w-4 h-4" />
        </Button>
      </div>

      {/* Kill Switch Confirmation Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent className="bg-[#0a0a0a] border-red-500/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-3 text-red-500">
              <AlertOctagon className="w-6 h-6" />
              Activate Emergency Kill Switch?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                This will <strong className="text-red-400">immediately stop ALL AI agent activities</strong>:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>All active campaigns will be paused</li>
                <li>All scheduled posts will be cancelled</li>
                <li>Content generation will stop</li>
                <li>No automated actions until you resume</li>
              </ul>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 mt-4">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-yellow-400">
                  You will need to manually reactivate campaigns after resuming.
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={activateKillSwitch}
              disabled={isLoading}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Stopping...
                </>
              ) : (
                <>
                  <PowerOff className="w-4 h-4 mr-2" />
                  ACTIVATE KILL SWITCH
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="glass border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Kill Switch History
            </DialogTitle>
            <DialogDescription>
              Audit trail of kill switch events
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto space-y-3">
            {history.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No events recorded yet</p>
            ) : (
              history.map((event, i) => (
                <div
                  key={event.id || i}
                  className={`p-3 rounded-lg border ${
                    event.event_type === "activated" || event.event_type === "auto_activated"
                      ? "bg-red-500/10 border-red-500/20"
                      : event.event_type === "deactivated"
                      ? "bg-green-500/10 border-green-500/20"
                      : "bg-white/5 border-white/10"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium capitalize ${
                      event.event_type === "activated" ? "text-red-400" :
                      event.event_type === "deactivated" ? "text-green-400" :
                      "text-yellow-400"
                    }`}>
                      {event.event_type.replace("_", " ")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(event.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{event.description}</p>
                  {event.affected_posts > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Posts affected: {event.affected_posts}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <span>By: {event.triggered_by}</span>
                    <span>•</span>
                    <span>IP: {event.ip_address}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
